/**
 * The routine's "day" is the participant's local calendar day, never the UTC date. East of
 * UTC the UTC date is still yesterday during the first hours of the morning, so a routine
 * done at 09:00 in Brisbane landed in the previous day's session and the streak never
 * advanced. Everything that names a day goes through here.
 */

/**
 * The local calendar day of `now` as YYYY-MM-DD, for a UTC offset in `Date#getTimezoneOffset`
 * minutes. The default is the host device's offset at `now` — the participant's own zone when
 * naming today; a stored attempt needs the offset of its session's zone (see `attemptDay`).
 */
export function localDayKey(now: Date, offsetMinutes = now.getTimezoneOffset()): string {
  return new Date(now.getTime() - offsetMinutes * 60_000).toISOString().slice(0, 10);
}

/**
 * The offset of an IANA time zone at `instant`, in the sign `Date#getTimezoneOffset` uses
 * (Brisbane → −600, New York in winter → 300), so DST is whatever the zone did that day.
 * Undefined where the engine cannot resolve the zone (`Intl.DateTimeFormat` without zone data).
 */
export function timeZoneOffsetMinutes(timeZone: string, instant: Date): number | undefined {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(instant);
    const field = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((part) => part.type === type)?.value);
    const wall = Date.UTC(
      field('year'),
      field('month') - 1,
      field('day'),
      field('hour') % 24,
      field('minute'),
      field('second'),
    );
    if (Number.isNaN(wall)) return undefined;
    // The parts carry no milliseconds; compare whole seconds.
    const seconds = instant.getTime() - instant.getMilliseconds();
    return Math.round((seconds - wall) / 60_000);
  } catch {
    return undefined;
  }
}

/** The device's IANA time zone, stored on every session so a day can be re-derived later. */
export function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  } catch {
    return 'UTC';
  }
}
